import { useEffect, useState } from "react";
import bundledResume from "../assets/resume.pdf";

/**
 * resumeStore
 *
 * The résumé link on the site points at whichever of these exists, in order:
 *
 *   1. a résumé configured in the admin panel (an external URL, or a PDF
 *      uploaded and stored in MongoDB)
 *   2. the PDF bundled at build time
 *
 * The fallback is the point: updating the résumé should never require a
 * deploy, but a cold database should never leave a dead download link.
 */

const API = "/api/resume";

/** Module-level cache so N components don't each make the request. */
let cached = null;
let inflight = null;

function load() {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = fetch(API)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      cached = data && data.url ? data : { url: bundledResume, mode: "bundled" };
      return cached;
    })
    .catch(() => {
      cached = { url: bundledResume, mode: "bundled" };
      return cached;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/**
 * Returns the current résumé URL. Starts at the bundled PDF so the link is
 * never dead on first paint, then upgrades if the admin has set one.
 */
export function useResumeUrl() {
  const [url, setUrl] = useState(cached?.url || bundledResume);

  useEffect(() => {
    let alive = true;
    load().then((d) => {
      if (alive && d?.url) setUrl(d.url);
    });
    return () => {
      alive = false;
    };
  }, []);

  return url;
}

export const BUNDLED_RESUME = bundledResume;

/* ---------------- admin-side helpers ---------------- */

export async function fetchResumeMeta() {
  const r = await fetch(API);
  if (!r.ok) throw new Error("Could not read résumé settings");
  return r.json();
}

export async function setResumeUrl(url) {
  const r = await fetch(API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode: "url", url }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || "Save failed");
  cached = null;
  return data;
}

/** Reads a File into base64 (without the data: prefix) and uploads it. */
export async function uploadResumeFile(file) {
  if (file.type !== "application/pdf") {
    throw new Error("Only PDF files are accepted");
  }
  if (file.size > 4 * 1024 * 1024) {
    throw new Error("PDF is larger than 4MB");
  }

  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.readAsDataURL(file);
  });

  const r = await fetch(API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      mode: "file",
      filename: file.name,
      mimeType: file.type,
      data: base64,
    }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || "Upload failed");
  cached = null;
  return data;
}

export async function clearResume() {
  const r = await fetch(API, { method: "DELETE" });
  if (!r.ok) throw new Error("Could not revert");
  cached = null;
  return r.json();
}
